/**
 * Pure rules of the "Reanalyze selected" dialog (project_management#514, #574).
 *
 * Kept out of the component so every dialog state is unit-tested without
 * rendering MUI.
 *
 * #574: a recording batch may span several patients. Who owns what is decided
 * by the SERVER preview (each target's owner from its DB row) — not by the
 * rows on the current page, which used to miss the off-page ids of "Select
 * all matching". The dialog therefore always previews with
 * `allowMultiplePatients: true` ("what would this be if allowed?") and only
 * sends `allowMultiplePatients: true` on create when the admin ticked the
 * "reanalyse together" confirmation. The server refuses an unconfirmed mixed
 * selection either way.
 */

export type ReanalysisScope = 'recording' | 'sequence';

export interface PatientBreakdown {
  userId: string;
  plannedCount: number;
  skippedCount: number;
}

export interface SkipReasonCount {
  reason: string;
  count: number;
}

export interface ReanalysisPreview {
  success: boolean;
  error?: string | null;
  plannedCount: number;
  concurrency: number;
  estimatedSeconds: number;
  estimatedDuration: string;
  patientCount?: number | null;
  multiPatient?: boolean | null;
  maxItems?: number | null;
  patients?: PatientBreakdown[] | null;
  skippedByReason?: SkipReasonCount[] | null;
  skipped?: Array<{ targetId: string; reason: string }> | null;
}

/** Patients listed in the dialog before collapsing into "and N more". */
export const MAX_LISTED_PATIENTS = 10;

export const previewVariables = (scope: ReanalysisScope, targetIds: string[]) => ({
  input: { scope, targetIds, reason: 'preview', allowMultiplePatients: true },
});

export const createVariables = (args: {
  scope: ReanalysisScope;
  targetIds: string[];
  reason: string;
  allowMultiplePatients: boolean;
}) => ({
  input: {
    scope: args.scope,
    targetIds: args.targetIds,
    reason: args.reason.trim(),
    allowMultiplePatients: args.allowMultiplePatients,
  },
});

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "6 recordings from 3 patients". */
export function selectionSummary(
  scope: ReanalysisScope,
  preview: Pick<ReanalysisPreview, 'plannedCount' | 'patientCount'>,
): string {
  return `${plural(preview.plannedCount, scope)} from ${plural(preview.patientCount ?? 0, 'patient')}`;
}

/** "2 × Already being analysed, 1 × Not found" (server counts; old servers: from the list). */
export function skipSummary(preview: Pick<ReanalysisPreview, 'skippedByReason' | 'skipped'>) {
  let counts = preview.skippedByReason ?? [];

  if (!counts.length && preview.skipped?.length) {
    const byReason: SkipReasonCount[] = [];

    preview.skipped.forEach(({ reason }) => {
      const row = byReason.find((entry) => entry.reason === reason);

      if (row) row.count += 1;
      else byReason.push({ reason, count: 1 });
    });
    counts = byReason;
  }

  return counts.map(({ reason, count }) => `${count} × ${reason}`).join(', ');
}

/**
 * Targets the selection WOULD run. On a refused preview `plannedCount` is 0
 * (nothing runs), so the size the admin has to narrow comes from the
 * per-patient rows.
 */
export function selectedRunnable(preview: Pick<ReanalysisPreview, 'plannedCount' | 'patients'>) {
  if (preview.plannedCount > 0) return preview.plannedCount;

  return (preview.patients ?? []).reduce((sum, row) => sum + row.plannedCount, 0);
}

export function skippedTotal(preview: Pick<ReanalysisPreview, 'skippedByReason' | 'skipped'>) {
  const fromCounts = (preview.skippedByReason ?? []).reduce((sum, row) => sum + row.count, 0);

  return fromCounts || preview.skipped?.length || 0;
}

/** The patients to list, and how many are left out. */
export function listedPatients(patients: PatientBreakdown[] | null | undefined) {
  const all = patients ?? [];

  return {
    rows: all.slice(0, MAX_LISTED_PATIENTS),
    hidden: Math.max(0, all.length - MAX_LISTED_PATIENTS),
  };
}

export type DialogState =
  | { kind: 'loading' }
  | { kind: 'refused'; error: string; hasBreakdown: boolean }
  | { kind: 'ready'; multiPatient: boolean };

export function dialogState(
  preview: ReanalysisPreview | null | undefined,
  loading: boolean,
): DialogState {
  if (loading || !preview) return { kind: 'loading' };

  if (!preview.success) {
    return {
      kind: 'refused',
      error: preview.error || 'This selection cannot be reanalysed.',
      hasBreakdown: (preview.patients?.length ?? 0) > 0,
    };
  }

  return { kind: 'ready', multiPatient: Boolean(preview.multiPatient) };
}

/**
 * Start is enabled only for a runnable preview, a reason, and — when the
 * selection spans patients — the explicit "reanalyse together" confirmation.
 */
export function canStart(args: {
  state: DialogState;
  reason: string;
  confirmedTogether: boolean;
  busy: boolean;
  plannedCount: number;
}): boolean {
  if (args.busy || args.state.kind !== 'ready') return false;
  if (!args.reason.trim() || args.plannedCount <= 0) return false;

  return !args.state.multiPatient || args.confirmedTogether;
}
